// QuizService - Serviço para gerenciar perguntas de quiz no Supabase
import { supabase } from '../lib/supabase';
import { runOnlineOrToast } from '../utils/networkGuard';

export interface QuizQuestion {
  id: string;
  question: string;
  options: string[]; // Array de opções (armazenado como JSONB)
  correctAnswer: number; // Índice da resposta correta
  explanation?: string;
  category: string;
  difficulty: 'easy' | 'medium' | 'hard';
  active: boolean;
  createdAt: string;
  updatedAt: string;
}

// Função helper para converter dados do banco para o formato da aplicação
function convertFromDB(dbData: any): QuizQuestion {
  return {
    id: dbData.id,
    question: dbData.question,
    options: Array.isArray(dbData.options) ? dbData.options : [],
    correctAnswer: dbData.correct_answer,
    explanation: dbData.explanation || undefined,
    category: dbData.category,
    difficulty: dbData.difficulty,
    active: dbData.active ?? true,
    createdAt: dbData.created_at,
    updatedAt: dbData.updated_at
  };
}

export interface QuizAttempt {
  id: string;
  userId: string;
  memberId?: string;
  questionId: string;
  selectedAnswer: number;
  isCorrect: boolean;
  pointsEarned: number;
  createdAt: string;
}

class QuizService {
  private static instance: QuizService;

  private constructor() {}

  static getInstance(): QuizService {
    if (!QuizService.instance) {
      QuizService.instance = new QuizService();
    }
    return QuizService.instance;
  }

  // ==================== QUIZ QUESTIONS ====================

  /**
   * Busca todas as perguntas ativas
   */
  async getAllActiveQuestions(): Promise<QuizQuestion[]> {
    const { data, error } = await supabase
      .from('quiz_questions')
      .select('id, question, options, correct_answer, explanation, category, difficulty, active, created_at, updated_at')
      .eq('active', true)
      .order('created_at', { ascending: false });

    if (error) {
      console.error('Erro ao buscar perguntas ativas:', error);
      throw error;
    }

    return (data || []).map(convertFromDB);
  }

  /**
   * Busca todas as perguntas (incluindo inativas) - ADMIN
   */
  async getAllQuestions(): Promise<QuizQuestion[]> {
    const { data, error } = await supabase
      .from('quiz_questions')
      .select('id, question, options, correct_answer, explanation, category, difficulty, active, created_at, updated_at')
      .order('created_at', { ascending: false });

    if (error) {
      console.error('Erro ao buscar todas as perguntas:', error);
      throw error;
    }

    return (data || []).map(convertFromDB);
  }

  /**
   * Busca pergunta por ID
   */
  async getQuestionById(id: string): Promise<QuizQuestion | null> {
    const { data, error } = await supabase
      .from('quiz_questions')
      .select('id, question, options, correct_answer, explanation, category, difficulty, active, created_at, updated_at')
      .eq('id', id)
      .single();

    if (error) {
      console.error('Erro ao buscar pergunta:', error);
      return null;
    }

    return data ? convertFromDB(data) : null;
  }

  /**
   * Busca perguntas por categoria
   */
  async getQuestionsByCategory(category: string): Promise<QuizQuestion[]> {
    const { data, error } = await supabase
      .from('quiz_questions')
      .select('id, question, options, correct_answer, explanation, category, difficulty, active, created_at, updated_at')
      .eq('category', category)
      .eq('active', true)
      .order('created_at', { ascending: false });

    if (error) {
      console.error('Erro ao buscar perguntas por categoria:', error);
      throw error;
    }

    return (data || []).map(convertFromDB);
  }

  /**
   * Busca perguntas por dificuldade
   */
  async getQuestionsByDifficulty(difficulty: 'easy' | 'medium' | 'hard'): Promise<QuizQuestion[]> {
    const { data, error } = await supabase
      .from('quiz_questions')
      .select('id, question, options, correct_answer, explanation, category, difficulty, active, created_at, updated_at')
      .eq('difficulty', difficulty)
      .eq('active', true)
      .order('created_at', { ascending: false });

    if (error) {
      console.error('Erro ao buscar perguntas por dificuldade:', error);
      throw error;
    }

    return (data || []).map(convertFromDB);
  }

  /**
   * Adiciona nova pergunta - ADMIN
   */
  async addQuestion(question: Omit<QuizQuestion, 'id' | 'createdAt' | 'updatedAt'>): Promise<QuizQuestion> {
    // Converte camelCase para snake_case para o banco
    const dbQuestion = {
      question: question.question,
      options: question.options,
      correct_answer: question.correctAnswer, // camelCase → snake_case
      explanation: question.explanation,
      category: question.category,
      difficulty: question.difficulty,
      active: question.active
    };

    const { data, error } = await runOnlineOrToast(async () =>
      supabase
        .from('quiz_questions')
        .insert([dbQuestion])
        .select('id, question, options, correct_answer, explanation, category, difficulty, active, created_at, updated_at')
        .single()
    );

    if (error) {
      console.error('Erro ao adicionar pergunta:', error);
      throw error;
    }

    // Converte snake_case de volta para camelCase
    return convertFromDB(data);
  }

  /**
   * Atualiza pergunta existente - ADMIN
   */
  async updateQuestion(id: string, updates: Partial<Omit<QuizQuestion, 'id' | 'createdAt' | 'updatedAt'>>): Promise<QuizQuestion> {
    // Converte camelCase para snake_case
    const dbUpdates: any = {};
    if (updates.correctAnswer !== undefined) dbUpdates.correct_answer = updates.correctAnswer;
    if (updates.question !== undefined) dbUpdates.question = updates.question;
    if (updates.options !== undefined) dbUpdates.options = updates.options;
    if (updates.explanation !== undefined) dbUpdates.explanation = updates.explanation;
    if (updates.category !== undefined) dbUpdates.category = updates.category;
    if (updates.difficulty !== undefined) dbUpdates.difficulty = updates.difficulty;
    if (updates.active !== undefined) dbUpdates.active = updates.active;

    const { data, error } = await runOnlineOrToast(async () =>
      supabase
        .from('quiz_questions')
        .update(dbUpdates)
        .eq('id', id)
        .select('id, question, options, correct_answer, explanation, category, difficulty, active, created_at, updated_at')
        .single()
    );

    if (error) {
      console.error('Erro ao atualizar pergunta:', error);
      throw error;
    }

    return convertFromDB(data);
  }

  /**
   * Deleta pergunta - ADMIN
   */
  async deleteQuestion(id: string): Promise<void> {
    const { error } = await runOnlineOrToast(async () =>
      supabase
        .from('quiz_questions')
        .delete()
        .eq('id', id)
    );

    if (error) {
      console.error('Erro ao deletar pergunta:', error);
      throw error;
    }
  }

  // ==================== QUIZ ATTEMPTS ====================
  // Nota: A tabela 'quiz_answer_attempts' foi criada via migration
  // Os tipos do Supabase serão atualizados após regenerar os tipos

  /**
   * Registra tentativa de resposta
   */
  async recordAttempt(attempt: Omit<QuizAttempt, 'id' | 'createdAt'>): Promise<QuizAttempt> {
    const { data, error } = await runOnlineOrToast(async () =>
      supabase
        .from('quiz_answer_attempts' as any)
        .insert([{
          user_id: attempt.userId,
          member_id: attempt.memberId || null,
          question_id: attempt.questionId,
          selected_answer: attempt.selectedAnswer,
          is_correct: attempt.isCorrect,
          points_earned: attempt.pointsEarned
        }])
        .select('id, user_id, member_id, question_id, selected_answer, is_correct, points_earned, created_at')
        .single()
    );

    if (error) {
      console.error('Erro ao registrar tentativa:', error);
      throw error;
    }

    return {
      id: data.id,
      userId: data.user_id,
      memberId: data.member_id || undefined,
      questionId: data.question_id,
      selectedAnswer: data.selected_answer,
      isCorrect: data.is_correct,
      pointsEarned: data.points_earned,
      createdAt: data.created_at
    };
  }

  async recordDailyCompletion(params: {
    userId: string;
    memberId: string;
    questionsAnswered: number;
    correctAnswers: number;
    totalQuestions: number;
    totalPoints: number;
    completionDate?: string;
    metadata?: Record<string, any>;
  }): Promise<void> {
    const completionIso = params.completionDate ?? new Date().toISOString();
    const quizDate = completionIso.split('T')[0];

    const payload = {
      user_id: params.userId,
      member_id: params.memberId,
      questions_answered: params.questionsAnswered,
      correct_answers: params.correctAnswers,
      total_questions: params.totalQuestions,
      score: params.totalPoints,
      completed: true,
      completed_at: completionIso,
      quiz_date: quizDate,
      metadata: params.metadata ?? {}
    };

    const { error } = await runOnlineOrToast(async () =>
      supabase
        .from('quiz_attempts' as any)
        .insert([payload])
    );

    if (error) {
      const isConflict = error.code === '23505' || error.details?.includes('duplicate key') || error.message?.includes('duplicate key');

      if (!isConflict) {
        console.error('Erro ao registrar conclusão diária do quiz:', error);
        throw error;
      }

      const { error: updateError } = await runOnlineOrToast(async () =>
        supabase
          .from('quiz_attempts' as any)
          .update({
            questions_answered: params.questionsAnswered,
            correct_answers: params.correctAnswers,
            total_questions: params.totalQuestions,
            score: params.totalPoints,
            completed: true,
            completed_at: completionIso,
            metadata: params.metadata ?? {}
          })
          .eq('member_id', params.memberId)
          .eq('quiz_date', quizDate)
          .eq('completed', true)
      );

      if (updateError) {
        console.error('Erro ao atualizar conclusão diária do quiz existente:', updateError);
        throw updateError;
      }
    }
  }

  /**
   * Busca tentativas de um membro em uma data específica
   */
  async getMemberAttemptsToday(memberId: string): Promise<QuizAttempt[]> {
    const today = new Date().toISOString().split('T')[0];
    
    const { data, error } = await supabase
      .from('quiz_answer_attempts' as any)
      .select('id, user_id, member_id, question_id, selected_answer, is_correct, points_earned, created_at')
      .eq('member_id', memberId)
      .gte('created_at', `${today}T00:00:00`)
      .lte('created_at', `${today}T23:59:59`)
      .order('created_at', { ascending: true });

    if (error) {
      console.error('Erro ao buscar tentativas do dia:', error);
      throw error;
    }

    return (data || []).map((d: any) => ({
      id: d.id,
      userId: d.user_id,
      memberId: d.member_id || undefined,
      questionId: d.question_id,
      selectedAnswer: d.selected_answer,
      isCorrect: d.is_correct,
      pointsEarned: d.points_earned,
      createdAt: d.created_at
    }));
  }

  /**
   * Verifica se membro já respondeu uma pergunta específica hoje
   */
  async hasAnsweredQuestionToday(memberId: string, questionId: string): Promise<boolean> {
    const today = new Date().toISOString().split('T')[0];
    
    const { data, error } = await supabase
      .from('quiz_answer_attempts' as any)
      .select('id')
      .eq('member_id', memberId)
      .eq('question_id', questionId)
      .gte('created_at', `${today}T00:00:00`)
      .lte('created_at', `${today}T23:59:59`)
      .limit(1);

    if (error) {
      console.error('Erro ao verificar resposta:', error);
      return false;
    }

    return (data || []).length > 0;
  }

  /**
   * Busca perguntas diárias para um membro (3 perguntas balanceadas)
   * Estratégia: 1 fácil, 1 média, 1 difícil de categorias diferentes
   */
  async getDailyQuestions(memberId: string): Promise<QuizQuestion[]> {
    // Buscar todas as perguntas ativas
    const allQuestions = await this.getAllActiveQuestions();
    
    // Buscar perguntas já respondidas nos últimos 7 dias
    const sevenDaysAgo = new Date();
    sevenDaysAgo.setDate(sevenDaysAgo.getDate() - 7);
    const recentAnsweredIds = await this.getRecentlyAnsweredQuestions(memberId, sevenDaysAgo);
    
    // Filtrar perguntas não respondidas recentemente
    const unanswered = allQuestions.filter(q => !recentAnsweredIds.includes(q.id));
    
    // Se não houver perguntas suficientes não respondidas, usar todas
    const availableQuestions = unanswered.length >= 3 ? unanswered : allQuestions;
    
    // Agrupar por dificuldade
    const easy = availableQuestions.filter(q => q.difficulty === 'easy');
    const medium = availableQuestions.filter(q => q.difficulty === 'medium');
    const hard = availableQuestions.filter(q => q.difficulty === 'hard');
    
    const selectedQuestions: QuizQuestion[] = [];
    const usedCategories = new Set<string>();
    
    // 1. Selecionar 1 pergunta fácil
    if (easy.length > 0) {
      const easyQuestion = this.selectRandomQuestionAvoidingCategories(easy, usedCategories);
      if (easyQuestion) {
        selectedQuestions.push(easyQuestion);
        usedCategories.add(easyQuestion.category);
      }
    }
    
    // 2. Selecionar 1 pergunta média
    if (medium.length > 0) {
      const mediumQuestion = this.selectRandomQuestionAvoidingCategories(medium, usedCategories);
      if (mediumQuestion) {
        selectedQuestions.push(mediumQuestion);
        usedCategories.add(mediumQuestion.category);
      }
    }
    
    // 3. Selecionar 1 pergunta difícil
    if (hard.length > 0) {
      const hardQuestion = this.selectRandomQuestionAvoidingCategories(hard, usedCategories);
      if (hardQuestion) {
        selectedQuestions.push(hardQuestion);
        usedCategories.add(hardQuestion.category);
      }
    }
    
    // Se não conseguiu 3 perguntas balanceadas, completar com perguntas aleatórias
    while (selectedQuestions.length < 3 && availableQuestions.length > 0) {
      const remaining = availableQuestions.filter(q => 
        !selectedQuestions.find(sq => sq.id === q.id)
      );
      
      if (remaining.length === 0) break;
      
      const randomQuestion = this.selectRandomQuestionAvoidingCategories(remaining, usedCategories);
      if (randomQuestion) {
        selectedQuestions.push(randomQuestion);
        usedCategories.add(randomQuestion.category);
      } else {
        // Se não encontrou evitando categorias, pegar qualquer uma
        const randomIndex = Math.floor(Math.random() * remaining.length);
        selectedQuestions.push(remaining[randomIndex]);
      }
    }
    
    // Embaralhar as perguntas selecionadas (Fisher-Yates shuffle)
    return this.fisherYatesShuffle(selectedQuestions);
  }
  
  /**
   * Fisher-Yates shuffle - Embaralhamento uniforme e não enviesado
   */
  private fisherYatesShuffle<T>(array: T[]): T[] {
    const shuffled = [...array];
    for (let i = shuffled.length - 1; i > 0; i--) {
      const j = Math.floor(Math.random() * (i + 1));
      [shuffled[i], shuffled[j]] = [shuffled[j], shuffled[i]];
    }
    return shuffled;
  }
  
  /**
   * Seleciona pergunta aleatória evitando categorias já usadas
   */
  private selectRandomQuestionAvoidingCategories(
    questions: QuizQuestion[], 
    usedCategories: Set<string>
  ): QuizQuestion | null {
    // Tentar encontrar pergunta de categoria não usada
    const differentCategory = questions.filter(q => !usedCategories.has(q.category));
    
    if (differentCategory.length > 0) {
      const randomIndex = Math.floor(Math.random() * differentCategory.length);
      return differentCategory[randomIndex];
    }
    
    // Se todas as categorias já foram usadas, retornar null
    return null;
  }
  
  /**
   * Busca IDs de perguntas respondidas recentemente (últimos N dias)
   */
  private async getRecentlyAnsweredQuestions(memberId: string, since: Date): Promise<string[]> {
    try {
      const { data, error } = await supabase
        .from('quiz_answer_attempts' as any)
        .select('question_id')
        .eq('member_id', memberId)
        .gte('created_at', since.toISOString());
      
      if (error) {
        console.error('Erro ao buscar perguntas recentes:', error);
        return [];
      }
      
      return (data || []).map((d: any) => d.question_id);
    } catch (error) {
      console.error('Erro ao buscar perguntas recentes:', error);
      return [];
    }
  }

  /**
   * Busca estatísticas de quiz de um membro
   */
  async getMemberQuizStats(memberId: string): Promise<{
    totalAttempts: number;
    correctAnswers: number;
    totalPoints: number;
    accuracy: number;
    attemptsToday: number;
  }> {
    // Total de tentativas
    const { count: totalAttempts } = await supabase
      .from('quiz_answer_attempts' as any)
      .select('id', { count: 'exact', head: true })
      .eq('member_id', memberId);

    // Respostas corretas
    const { count: correctAnswers } = await supabase
      .from('quiz_answer_attempts' as any)
      .select('id', { count: 'exact', head: true })
      .eq('member_id', memberId)
      .eq('is_correct', true);

    // Total de pontos
    const { data: pointsData } = await supabase
      .from('quiz_answer_attempts' as any)
      .select('points_earned')
      .eq('member_id', memberId);

    const totalPoints = (pointsData || []).reduce((sum: number, item: any) => sum + item.points_earned, 0);

    // Tentativas hoje
    const today = new Date().toISOString().split('T')[0];
    const { count: attemptsToday } = await supabase
      .from('quiz_answer_attempts' as any)
      .select('id', { count: 'exact', head: true })
      .eq('member_id', memberId)
      .gte('created_at', `${today}T00:00:00`)
      .lte('created_at', `${today}T23:59:59`);

    const accuracy = totalAttempts && totalAttempts > 0 
      ? ((correctAnswers || 0) / totalAttempts) * 100 
      : 0;

    return {
      totalAttempts: totalAttempts || 0,
      correctAnswers: correctAnswers || 0,
      totalPoints,
      accuracy,
      attemptsToday: attemptsToday || 0
    };
  }

  /**
   * Busca estatísticas globais do quiz
   */
  async getGlobalStats(): Promise<{
    totalQuestions: number;
    activeQuestions: number;
    totalAttempts: number;
    averageAccuracy: number;
  }> {
    // Total de perguntas
    const { count: totalQuestions } = await supabase
      .from('quiz_questions')
      .select('id', { count: 'exact', head: true });

    // Perguntas ativas
    const { count: activeQuestions } = await supabase
      .from('quiz_questions')
      .select('id', { count: 'exact', head: true })
      .eq('active', true);

    // Total de tentativas
    const { count: totalAttempts } = await supabase
      .from('quiz_answer_attempts' as any)
      .select('id', { count: 'exact', head: true });

    // Calcular accuracy média
    const { count: correctAttempts } = await supabase
      .from('quiz_answer_attempts' as any)
      .select('id', { count: 'exact', head: true })
      .eq('is_correct', true);

    const averageAccuracy = totalAttempts && totalAttempts > 0
      ? ((correctAttempts || 0) / totalAttempts) * 100
      : 0;

    return {
      totalQuestions: totalQuestions || 0,
      activeQuestions: activeQuestions || 0,
      totalAttempts: totalAttempts || 0,
      averageAccuracy
    };
  }
}

export default QuizService;
